import React, { useState } from 'react';
import {
  TextField,
  Typography,
  Box,
  Grid,
  Avatar,
  Button,
  Stack,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import validator from 'validator';
import { Navigate, useNavigate } from 'react-router-dom';
import { GoogleAddressField } from '@pv/common/components';
import { enqueueSnackbar } from 'notistack';
import { Fields } from '@pv/common/components';

const EmailInput = ({
  confirmed,
  unconfirmedEmail,
  confirmationSent,
  value,
  handleChange,
  error,
  touched,
  handleResendConfirmation,
  currentEmail,
}: {
  confirmed: boolean;
  unconfirmedEmail?: string;
  confirmationSent: boolean;
  value: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  touched: boolean;
  handleResendConfirmation: () => void;
  currentEmail: string;
}) => {
  return (
    <>
      <TextField
        fullWidth
        label="Email"
        name="email"
        value={value}
        onChange={handleChange}
        error={touched && Boolean(error)}
        helperText={touched && error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {confirmed && !unconfirmedEmail ? (
                <CheckCircle color="success" />
              ) : (
                <Warning color="warning" />
              )}
            </InputAdornment>
          ),
        }}
        required
      />
      {(!confirmed || unconfirmedEmail) && (
        <Stack direction="column" spacing={0.5} mt={1} alignItems="flex-start">
          {unconfirmedEmail && confirmed && (
            <Typography variant="caption" component="p">
              Your currently confirmed email is <strong>{currentEmail}</strong>.
            </Typography>
          )}
          <Typography variant="caption" component="p">
            {unconfirmedEmail
              ? `You've requested to change your email to ${unconfirmedEmail}. Please confirm it via the link sent to that address.`
              : `Please confirm your email address (${currentEmail}) to gain full access.`}
          </Typography>
          {!confirmationSent && (
            <MuiLink
              variant="caption"
              component="button"
              onClick={(e) => {
                e.preventDefault();
                handleResendConfirmation();
              }}
            >
              Resend confirmation email?
            </MuiLink>
          )}
        </Stack>
      )}
    </>
  );
};

export const GuestProfileForm = () => {
  const { guest, updateGuest, error, resendConfirmation } = useAuth();
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const uploadedImageUrl = uploadedImage
    ? URL.createObjectURL(uploadedImage)
    : null;
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      firstName: guest?.firstName || '',
      lastName: guest?.lastName || '',
      email: guest?.unconfirmedEmail || guest?.email || '',
      phone: guest?.phone || '',
      address: {
        streetAddress1: guest?.address?.streetAddress1 || '',
        streetAddress2: guest?.address?.streetAddress2 || '',
        city: guest?.address?.city || '',
        state: guest?.address?.state || '',
        zip: guest?.address?.zip || '',
        country: guest?.address?.country || '',
        formattedAddress: guest?.address?.formattedAddress || '',
      },
      image: uploadedImage || null,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      phone: Yup.string()
        .required('Phone is required')
        .test('is-mobile-phone', 'Enter a valid phone number', (value) => {
          if (!value) return false;
          // Remove all non-digit characters (parentheses, dashes, spaces)
          const cleanPhone = value.replace(/\D/g, '');
          return validator.isMobilePhone(cleanPhone);
        }),
      address: Yup.object({
        streetAddress1: Yup.string().optional().nullable(),
        streetAddress2: Yup.string().optional().nullable(),
        city: Yup.string().optional().nullable(),
        state: Yup.string().optional().nullable(),
        zip: Yup.string().optional().nullable(),
        country: Yup.string().optional().nullable(),
        formattedAddress: Yup.string().optional().nullable(),
        placeId: Yup.string().optional().nullable(),
      })
        .optional()
        .nullable(),
      image: Yup.mixed().optional().nullable(),
    }),
    onSubmit: (values) => {
      const input = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        address: values.address,
        image: values.image,
      };
      updateGuest(input);
      formik.setSubmitting(false);
      formik.setTouched({}, false);
      formik.setFieldValue('email', values.email, false);
      navigate('/');
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formik.isValid) {
      formik.handleSubmit();
    } else {
      formik.setSubmitting(false);
      formik.setErrors(formik.errors);
    }
  };

  const handleResendConfirmation = () => {
    const recipientEmail = guest?.unconfirmedEmail || guest?.email;

    if (!recipientEmail) {
      return;
    }

    resendConfirmation(recipientEmail);
    setConfirmationSent(true);
    enqueueSnackbar('Confirmation email sent', {
      variant: 'success',
    });
  };

  if (!guest) return <Navigate to="/sign-in" />;

  const { confirmed, unconfirmedEmail, email: currentEmail } = guest;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        {guest.firstName
          ? 'Update your profile'
          : 'Welcome! Tell us about yourself'}
      </Typography>
      {error && (
        <Typography color="error" align="center">
          {error}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={uploadedImageUrl || guest?.gravatarUrl}
              sx={{ width: 100, height: 100 }}
            />
            <Button variant="contained" color="secondary" component="label">
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    formik.setFieldValue('image', file);
                    setUploadedImage(file);
                  }
                }}
              />
            </Button>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <EmailInput
            confirmed={confirmed}
            unconfirmedEmail={unconfirmedEmail}
            confirmationSent={confirmationSent}
            value={formik.values.email}
            handleChange={formik.handleChange}
            error={formik.errors.email}
            touched={formik.touched.email ?? false}
            handleResendConfirmation={handleResendConfirmation}
            currentEmail={currentEmail}
          />
        </Grid>

        <Grid item xs={12}>
          <Fields.PvPhoneField
            fullWidth
            label="Phone"
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <GoogleAddressField
            value={formik.values.address}
            onChange={(address) => formik.setFieldValue('address', address)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
            type="submit"
            variant="contained"
            color="secondary"
            fullWidth
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
